import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import { faVideo, faSpinner, faArrowLeft, faSearch, faXmark, faGraduationCap, faBook, faBrain, faCheck, faClock, faExclamationTriangle, faUpload, faCloudArrowUp, faCog, faEyeSlash } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, formatDuration } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Video, Course, Module, CourseID, ModuleID, VideoProcessingState } from '@/models';

const PAGE_SIZE = 50;

function getProcessingStateInfo(state: VideoProcessingState): { icon: typeof faCheck; color: string; label: string } {
  switch (state) {
    case 'DONE':
      return { icon: faCheck, color: 'text-green-600 dark:text-green-400', label: 'Fertig' };
    case 'ACTIVE':
      return { icon: faCog, color: 'text-blue-600 dark:text-blue-400 animate-spin', label: 'Aktiv' };
    case 'QUEUED':
      return { icon: faClock, color: 'text-yellow-600 dark:text-yellow-400', label: 'Warteschlange' };
    case 'WAITING':
      return { icon: faUpload, color: 'text-gray-600 dark:text-gray-400', label: 'Wartend' };
    case 'ERROR':
      return { icon: faExclamationTriangle, color: 'text-red-600 dark:text-red-400', label: 'Fehler' };
    default:
      return { icon: faClock, color: 'text-gray-600 dark:text-gray-400', label: state };
  }
}

export default function AdminAllVideosList() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [courseMap, setCourseMap] = useState<Map<CourseID, Course>>(new Map());
  const [moduleMap, setModuleMap] = useState<Map<ModuleID, Module>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [criticalError, setCriticalError] = useState<boolean>(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [providesKnowledgeFilter, setProvidesKnowledgeFilter] = useState<boolean | null>(null);

  // Load courses for dropdown
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await APIRoutes.listCourses({ limit: 1000 });
        const map = new Map<CourseID, Course>();
        for (const course of response.courses) {
          map.set(course.id, course);
        }
        setCourseMap(map);
      } catch (err) {
        console.error('Error loading courses for filter:', err);
      }
    };
    loadCourses();
  }, []);

  // Load modules when course is selected (or all modules)
  useEffect(() => {
    const loadModules = async () => {
      try {
        const response = await APIRoutes.listAllModules({ limit: 1000 });
        const map = new Map<ModuleID, Module>();
        for (const module of response.modules) {
          map.set(module.id, module);
        }
        setModuleMap(map);
      } catch (err) {
        console.error('Error loading modules for filter:', err);
      }
    };
    loadModules();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, providesKnowledgeFilter]);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listAllVideos({
        limit: PAGE_SIZE,
        next: cursorToken,
        search: (debouncedSearchTerm.trim() === '') ? undefined : debouncedSearchTerm,
        providesKnowledge: providesKnowledgeFilter ?? undefined,
        includeHiddenByModuleLimit: true,
      });

      setVideos(response.videos || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Critical error loading videos list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Videos');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, providesKnowledgeFilter]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setProvidesKnowledgeFilter(null);
  };

  const hasActiveFilters = searchTerm !== '' || providesKnowledgeFilter !== null;

  if (criticalError) {
    return (
      <FullscreenError
        title="Videoliste nicht verfügbar"
        message={error || 'Die Videoliste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchVideos}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faVideo} className="w-8 h-8 text-purple-600 dark:text-purple-400 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Alle Videos ({total})
            </h1>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              />
              <input
                type="text"
                placeholder="Suche nach Titel oder Beschreibung..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm !== '' && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-4">

            {/* Knowledge Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                KI-Wissen
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setProvidesKnowledgeFilter(providesKnowledgeFilter === true ? null : true)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    providesKnowledgeFilter === true
                      ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  <FontAwesomeIcon icon={faBrain} className="w-3 h-3 mr-1" />
                  Mit Wissen
                </button>
                <button
                  onClick={() => setProvidesKnowledgeFilter(providesKnowledgeFilter === false ? null : false)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    providesKnowledgeFilter === false
                      ? 'bg-gray-200 text-gray-800 border-gray-400 dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  Ohne Wissen
                </button>
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="flex justify-end">
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error && !criticalError && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {videos.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faVideo} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {hasActiveFilters ? 'Keine Videos gefunden, die den Filterkriterien entsprechen' : 'Keine Videos gefunden'}
          </p>
        </div>
      )}

      {videos.length > 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Titel
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kurs / Modul
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Dauer
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Verarbeitung
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {videos.map((video) => {
                  const course = courseMap.get(video.courseID);
                  const module = moduleMap.get(video.moduleID);
                  const localState = getProcessingStateInfo(video.localProcessingState);
                  const uploadState = getProcessingStateInfo(video.cdnUploadState);
                  const cdnState = getProcessingStateInfo(video.cdnProcessingState);

                  return (
                    <tr key={video.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700${video.hiddenByModuleLimit ? ' opacity-20' : ''}`}>
                      <td className="px-6 py-4">
                        <Link to={`/admin/courses/${video.courseID}/modules/${video.moduleID}`} className="block">
                          <div className="flex items-center">
                            <div className={`text-sm font-medium text-gray-900 dark:text-white${video.hiddenByModuleLimit ? ' line-through' : ''}`}>
                              {video.globalIndex && <span className="text-gray-500 dark:text-gray-400 mr-2">{video.globalIndex}</span>}
                              {video.title}
                            </div>
                            {video.hiddenByModuleLimit && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200 ml-2">
                                <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3 mr-1" />
                                Ausgeblendet
                              </span>
                            )}
                            {video.providesKnowledge && (
                              <FontAwesomeIcon icon={faBrain} className="w-3 h-3 ml-2 text-blue-500" title="Hat KI-Wissen" />
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1 w-80">
                          <Link to={`/admin/courses/${video.courseID}`} className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                            <FontAwesomeIcon icon={faGraduationCap} className="w-3 h-3 mr-1" />
                            <span className="line-clamp-1">{course?.title || 'Unbekannt'}</span>
                          </Link>
                          <Link to={`/admin/courses/${video.courseID}/modules/${video.moduleID}`} className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                            <FontAwesomeIcon icon={faBook} className="w-3 h-3 mr-1" />
                            <span className="line-clamp-1">{module?.title || 'Unbekannt'}</span>
                          </Link>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${video.courseID}/modules/${video.moduleID}`} className="block">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            video.published
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                          }`}>
                            {video.published ? 'Veröffentlicht' : 'Entwurf'}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${video.courseID}/modules/${video.moduleID}`} className="block">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {video.duration !== null ? formatDuration(video.duration) : '-'}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${video.courseID}/modules/${video.moduleID}`} className="block">
                          <div className="flex items-center space-x-2">
                            <span title={`Lokal: ${localState.label}`}>
                              <FontAwesomeIcon icon={faCog} className={`w-3 h-3 ${localState.color}`} />
                            </span>
                            <span title={`Upload: ${uploadState.label}`}>
                              <FontAwesomeIcon icon={faCloudArrowUp} className={`w-3 h-3 ${uploadState.color}`} />
                            </span>
                            <span title={`CDN: ${cdnState.label}`}>
                              <FontAwesomeIcon icon={faVideo} className={`w-3 h-3 ${cdnState.color}`} />
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${video.courseID}/modules/${video.moduleID}`} className="block">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(video.created)}
                          </div>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}
    </>
  );
}
